import * as THREE from "three";
import { CENSUS_BANDS, CENSUS_KINDS, type CensusKind, type CensusTable, type CensusWalker } from "./perf";

/**
 * Scene census — the three-aware half of the perf overlay's draw-distance
 * table. Walks every visible mesh, classifies it, measures its distance from
 * the eye (nearest point of its world bounding sphere) and files its triangles
 * by kind × distance band. Only meshes whose bounds intersect the camera
 * frustum count, so the table approximates what the main pass SUBMITS rather
 * than what is merely resident. Shadow casters are tallied separately, any
 * band, because the shadow pass has its own (light-space) frustum.
 *
 * Costs one traversal; walked at paint time (~4 Hz), never per frame.
 */
const KIDX = Object.fromEntries(CENSUS_KINDS.map((k, i) => [k, i])) as Record<CensusKind, number>;

const _frustum = new THREE.Frustum();
const _m = new THREE.Matrix4();
const _sphere = new THREE.Sphere();
const _eye = new THREE.Vector3();

type Tagged = THREE.Mesh & {
  userData: Record<string, unknown> & { censusKind?: CensusKind };
  isInstancedMesh?: boolean;
  isSkinnedMesh?: boolean;
  count?: number;
};

function classify(o: Tagged): CensusKind {
  const u = o.userData;
  if (u.censusKind) return u.censusKind;
  if (u.blades) return "grass";
  if (u.alpineCover || u.meadowCover || u.forestFloor || u.wetlandCover || u.coastalCover) return "cover";
  if (u.rockGroups) return "rock";
  if (o.isSkinnedMesh) return "actor";
  const geo = o.geometry;
  if (geo?.getAttribute("field")) return "terrain";
  const mat = o.material as THREE.Material | THREE.Material[] | undefined;
  const single = Array.isArray(mat) ? mat[0] : mat;
  if (single?.userData?.waterUniforms || o.name === "riverRibbons") return "water";
  if (o.isInstancedMesh) return u.scatterDetailed || u.fadeNear ? "tree" : "scatter";
  const n = o.name;
  if (n.startsWith("settlement-") || n.startsWith("town-") || n.startsWith("hs-")) return "building";
  if (n === "aurora-curtains" || n.startsWith("cloud") || n.startsWith("sky") || n.startsWith("atmo")) return "sky";
  return "other";
}

function triangles(o: Tagged): { tris: number; instances: number } {
  const geo = o.geometry as THREE.BufferGeometry & { instanceCount?: number; isInstancedBufferGeometry?: boolean };
  if (!geo) return { tris: 0, instances: 0 };
  const verts = geo.index ? geo.index.count : geo.getAttribute("position")?.count ?? 0;
  const per = geo.drawRange.count !== Infinity ? Math.min(geo.drawRange.count, verts) / 3 : verts / 3;
  let instances = 1;
  if (o.isInstancedMesh) instances = o.count ?? 1;
  else if (geo.isInstancedBufferGeometry) instances = geo.instanceCount === Infinity || geo.instanceCount === undefined ? 1 : geo.instanceCount;
  return { tris: per * instances, instances };
}

/** Build a walker bound to the live scene + camera. */
export function makeRenderCensus(scene: THREE.Scene, camera: THREE.Camera): CensusWalker {
  return (out: CensusTable) => {
    camera.updateMatrixWorld();
    _m.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_m);
    _eye.setFromMatrixPosition(camera.matrixWorld);
    scene.traverseVisible((obj) => {
      const o = obj as Tagged;
      if (!(o as THREE.Mesh).isMesh || !o.geometry) return;
      const geo = o.geometry;
      if (!geo.boundingSphere) geo.computeBoundingSphere();
      if (!geo.boundingSphere) return;
      // Instanced scatter keeps its whole-batch bounds on the MESH (scatterLod),
      // the geometry's sphere being the unit template's.
      const src = (o as THREE.InstancedMesh).boundingSphere ?? geo.boundingSphere;
      _sphere.copy(src).applyMatrix4(o.matrixWorld);
      const k = KIDX[classify(o)];
      const { tris, instances } = triangles(o);
      if (o.castShadow) { out.casterTris[k] += tris; out.casterMeshes[k]++; }
      if (o.frustumCulled && !_frustum.intersectsSphere(_sphere)) return;
      const nearest = Math.max(0, _sphere.center.distanceTo(_eye) - _sphere.radius);
      let b = 0;
      while (b < CENSUS_BANDS.length - 1 && nearest >= CENSUS_BANDS[b]) b++;
      out.tris[k][b] += tris;
      out.meshes[k][b]++;
      out.instances[k][b] += instances;
    });
  };
}
